import styles from "./CalculationDetails.module.css";
import { type JSX, RefObject, useContext } from "react";
import {
  AppContext,
  getLabelForInspection,
  getLabelForInspectionType,
  resolveActiveServices,
  selectPaidServices,
  selectSubsidyDependantCalculationDetails,
  useBasepriceForResidual,
} from "../../state";
import { useI18n } from "../../i18n";
import { Heading } from "../Heading";
import classNames from "classnames";
import arrow_down from "../../assets/icons/keyboard_arrow_down.svg";
import { MonthlyRateDetailsRow } from "../MonthlyRateDetailsRow";
import { Abbreviation } from "../Abbreviaton";
import { ExpandableAmountRow, PlainAmountRow, RichLabelAmountRow } from "../AmountRow";
import { TaxAbbreviations } from "./TaxAbbreviations.tsx";
import { RentenFaqLink } from "../RentenFaqLink";
import { Trans } from "react-i18next";
import { Details } from "../Details";

type CalculationDetailsProps = {
  isExpanded: boolean;
  onToggle: (showDetails: boolean) => void;
  monthlyRateRef: RefObject<HTMLDivElement | null>;
};

const CalculationDetails = ({ isExpanded, onToggle, monthlyRateRef }: CalculationDetailsProps): JSX.Element => {
  const { t, formatNumber } = useI18n();
  const state = useContext(AppContext);
  const controlClasses = classNames(styles.control, {
    [styles.expanded]: isExpanded,
  });
  const detailsContentClass = classNames(styles.calculationDetailsContent, {
    [styles.collapsed]: !state.calculation.showResultsDetails,
  });
  const detailsContentWithFadeClass = classNames(styles.calculationDetailsContent, {
    [styles.faded]: !state.calculation.showResultsDetails,
  });

  const subsidyDependantCalculationDetails = selectSubsidyDependantCalculationDetails(state);
  const { companyPays, employeePays } = selectPaidServices(state);

  const basePriceForResidual = useBasepriceForResidual(state.configuration);

  const activeService = resolveActiveServices(employeePays, companyPays);
  const hasActiveService = !!activeService;
  const possibleDailyContributions =
    state.calculation.calculationDetails?.calculationDetailsWithSubsidy?.possibleDailyContributions ?? 0;

  const showVormiete = state.configuration.elementVisibility.vormieteInfo;

  const netChargeEmployee = state.calculation.calculationDetails?.calculationDetailsWithSubsidy?.netChargeEmployee ?? 0;
  const labelForInspectionCompany = getLabelForInspection(t, companyPays);

  const labelForInspectionActiveService = getLabelForInspectionType(t, activeService);
  const servicesMonthlyRate = getDisplayedServiceMonthlyRate(
    subsidyDependantCalculationDetails?.servicesMonthlyRate,
    state.configuration.frameContract.grant ?? 0,
    state.configuration.deductGrantFromServicePrices,
  );

  const fadedContentClickHandler = () => onToggle(true);
  const fadedContentKeyDownHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.code === "Space") {
      event.preventDefault();
      onToggle(true);
    }
  };

  return (
    <>
      <div>
        <Heading as="h2">
          <button
            id="JobRadRechner_Details"
            className={controlClasses}
            onClick={() => {
              onToggle(!isExpanded);
            }}
            aria-expanded={isExpanded}
            type="button"
          >
            <span className={styles.label}>{t("calculationDetails.sectionHeading")}</span>
            <span className={styles.icon}>
              <img src={arrow_down} alt="" />
            </span>
          </button>
        </Heading>
      </div>
      <div
        className={detailsContentWithFadeClass}
        tabIndex={state.calculation.showResultsDetails ? undefined : 0}
        onClick={state.calculation.showResultsDetails === false ? fadedContentClickHandler : undefined}
        onKeyDown={state.calculation.showResultsDetails === false ? fadedContentKeyDownHandler : undefined}
        data-testid="faded-teaser"
        id="calculationDetailsTeaser"
      >
        <div
          aria-hidden={state.calculation.showResultsDetails === false}
          inert={state.calculation.showResultsDetails === false}
        >
          <Heading as="h3">{t("calculationDetails.bikeAsSalary.1")}</Heading>
          <p className={styles.description}>{t("calculationDetails.bikeAsSalary.2")}</p>
        </div>
        <table
          className={styles.calculationDetailsTable}
          aria-hidden={state.calculation.showResultsDetails === false}
          inert={state.calculation.showResultsDetails === false}
        >
          <tbody>
            <PlainAmountRow
              labelKey={"calculationDetails.bikeAsSalary.3"}
              value={subsidyDependantCalculationDetails?.grossSalary}
            />
            <ExpandableAmountRow
              label={t("calculationDetails.bikeAsSalary.4")}
              value={presentAsNegative(subsidyDependantCalculationDetails?.totalMonthlyRate)}
              id="calculationDetailsExpandableRow-totalMonthlyRate"
            >
              <div className={styles.expandedContentList}>
                <table className={styles.subTable}>
                  <tbody>
                    <MonthlyRateDetailsRow
                      rowLabel={t("calculationDetails.bikeAsSalary.9")}
                      rowValue={presentAsNegative(subsidyDependantCalculationDetails?.leasingMonthlyRate)}
                    />

                    {hasActiveService ? (
                      <MonthlyRateDetailsRow
                        rowLabel={labelForInspectionActiveService ?? ""}
                        rowValue={presentAsNegative(servicesMonthlyRate)}
                      />
                    ) : null}

                    <MonthlyRateDetailsRow
                      rowLabel={t("calculationDetails.bikeAsSalary.12")}
                      rowValue={presentAsNegative(subsidyDependantCalculationDetails?.insuranceMonthlyRate)}
                    />

                    {state.configuration.elementVisibility.overallUsageRate && (
                      <PlainAmountRow
                        label={t("calculationDetails.overallUsageRate")}
                        value={presentAsNegative(subsidyDependantCalculationDetails?.overallUsageRate)}
                        isTotal={true}
                        variant={"small"}
                      />
                    )}
                    {state.configuration.elementVisibility.subsidyBreakdownRow &&
                      state.calculation.calculationDetails?.hasSubsidy && (
                        <MonthlyRateDetailsRow
                          rowLabel={t("calculationDetails.bikeAsSalary.15")}
                          rowValue={
                            state.calculation.calculationDetails?.calculationDetailsWithSubsidy?.subsidyGrossResolved
                          }
                          rowDetail={t("calculationDetails.bikeAsSalary.14")}
                          highlightDetails={true}
                        />
                      )}

                    {hasActiveService && companyPays?.inspection ? (
                      <MonthlyRateDetailsRow
                        rowLabel={labelForInspectionCompany ?? ""}
                        rowValue={subsidyDependantCalculationDetails?.servicesMonthlyRate}
                        rowDetail={t("calculationDetails.bikeAsSalary.14")}
                        highlightDetails={true}
                      />
                    ) : null}

                    {companyPays?.insurance ? (
                      <MonthlyRateDetailsRow
                        rowLabel={t("calculationDetails.bikeAsSalary.12")}
                        rowValue={subsidyDependantCalculationDetails?.insuranceMonthlyRate}
                        rowDetail={t("calculationDetails.bikeAsSalary.14")}
                        highlightDetails={true}
                      />
                    ) : null}

                    <PlainAmountRow
                      label={t("calculationDetails.monthlyBruttoRate")}
                      value={presentAsNegative(subsidyDependantCalculationDetails?.totalMonthlyRate)}
                      isTotal={true}
                      variant={"small"}
                    />
                  </tbody>
                </table>
              </div>
            </ExpandableAmountRow>
            <ExpandableAmountRow
              label={t("calculationDetails.bikeAsSalary.5")}
              value={subsidyDependantCalculationDetails?.valuationPrivateUse}
              id="calculationDetailsExpandableRow-valuationPrivateUse"
            >
              {state.calculation.userInput?.arbeitnehmerTyp === "beamte" ? (
                <p>{t("calculationDetails.bikeAsSalary.6.beamte")}</p>
              ) : (
                <p>{t("calculationDetails.bikeAsSalary.6")}</p>
              )}
              <p>
                {t("calculationDetails.bikeAsSalary.7", {
                  taxationFactor: formatNumber(state.configuration.leasingSetting.taxationFactor, { digits: 2 }),
                })}
              </p>
            </ExpandableAmountRow>
          </tbody>
          <tfoot>
            <PlainAmountRow
              label={t("calculationDetails.bikeAsSalary.8")}
              isTotal
              value={subsidyDependantCalculationDetails?.newTaxableIncome}
            />
          </tfoot>
        </table>
      </div>
      <div className={detailsContentClass}>
        <div>
          {state.calculation.userInput?.arbeitnehmerTyp === "angestellte" ? (
            <>
              <Heading as="h3">{t("calculationDetails.feesAndTaxes.lessTaxes")}</Heading>
              <p className={styles.description}>{t("calculationDetails.feesAndTaxes.lessGross")}</p>
            </>
          ) : (
            <>
              <Heading as="h3">{t("calculationDetails.taxes.lessTaxes")}</Heading>
              <p className={styles.description}>{t("calculationDetails.taxes.lessGross")}</p>
            </>
          )}
        </div>
        <table className={styles.calculationDetailsTable}>
          <tbody>
            {state.calculation.userInput?.arbeitnehmerTyp === "angestellte" && (
              <>
                <RichLabelAmountRow
                  htmlLabel={
                    <label>
                      {t("calculationDetails.feesAndTaxes.duties")}
                      (<Abbreviation short="RV" long={t("calculationDetails.feesAndTaxes.RV")} />,{" "}
                      <Abbreviation short="AV" long={t("calculationDetails.feesAndTaxes.AV")} />,{" "}
                      <Abbreviation short="KV" long={t("calculationDetails.feesAndTaxes.KV")} />,{" "}
                      <Abbreviation short="PV" long={t("calculationDetails.feesAndTaxes.PV")} />)
                    </label>
                  }
                  value={presentAsNegative(subsidyDependantCalculationDetails?.socialInsurances)}
                />
                <tr>
                  <td colSpan={2}>
                    <RentenFaqLink calculatorEnv={state.configuration.calculatorEnv} />
                  </td>
                </tr>
              </>
            )}
            <RichLabelAmountRow
              htmlLabel={
                <label>
                  {t("calculationDetails.feesAndTaxes.taxes")}
                  (
                  <TaxAbbreviations
                    hasChurchTax={subsidyDependantCalculationDetails?.hasChurchTax ?? false}
                    hasSolidarityContribution={subsidyDependantCalculationDetails?.hasSolidarityContribution ?? false}
                  />
                  )
                </label>
              }
              value={subsidyDependantCalculationDetails?.taxes && -subsidyDependantCalculationDetails?.taxes}
            />
            <ExpandableAmountRow
              label={t("calculationDetails.feesAndTaxes.privateUse")}
              value={presentAsNegative(subsidyDependantCalculationDetails?.valuationPrivateUse)}
              id="calculationDetailsExpandableRow-valuationPrivateUseFeesAndTaxes"
            >
              {state.calculation.userInput?.arbeitnehmerTyp === "beamte" ? (
                <p>{t("calculationDetails.feesAndTaxes.noMoney.beamte")}</p>
              ) : (
                <p>{t("calculationDetails.feesAndTaxes.noMoney")}</p>
              )}
            </ExpandableAmountRow>
          </tbody>
          <tfoot>
            <PlainAmountRow
              label={t("calculationDetails.feesAndTaxes.newNet")}
              isTotal
              value={subsidyDependantCalculationDetails?.newNetSalary}
            />
          </tfoot>
        </table>
      </div>
      <div className={detailsContentClass} ref={monthlyRateRef}>
        <div>
          <Heading as="h3">{t("calculationDetails.paysMonthly.1")}</Heading>
          <p className={styles.description}>{t("calculationDetails.paysMonthly.2")}</p>
        </div>
        <table className={styles.calculationDetailsTable}>
          <tbody>
            <PlainAmountRow
              labelKey={"calculationDetails.paysMonthly.3"}
              value={subsidyDependantCalculationDetails?.oldNetSalary}
            />
            <PlainAmountRow
              labelKey={"calculationDetails.paysMonthly.4"}
              value={presentAsNegative(subsidyDependantCalculationDetails?.newNetSalary)}
            />
          </tbody>
          <tfoot>
            <PlainAmountRow
              label={t("calculationDetails.paysMonthly.5")}
              isTotal
              value={subsidyDependantCalculationDetails?.netChargeEmployee}
            />
          </tfoot>
        </table>
      </div>
      {showVormiete ? (
        <div className={detailsContentClass}>
          <div>
            <Heading as="h3">{t("calculationDetails.beforeTheContract.title")}</Heading>
            <p className={styles.beforeExample}>
              <Trans i18nKey={"calculationDetails.beforeTheContract.explanation"} />
            </p>
            <Details
              summary={t("calculationDetails.beforeTheContract.exampleTitle")}
              id={"calculationDetailsExpandableArea-vormiete"}
            >
              <p>
                <Trans
                  i18nKey={"calculationDetails.beforeTheContract.example"}
                  values={{
                    completeMonthlyRate: formatNumber(netChargeEmployee, { digits: 2 }),
                    possibleDailyContribution: formatNumber(possibleDailyContributions, { digits: 2 }),
                    possibleDailyContributionsTimes3: formatNumber(possibleDailyContributions * 3, { digits: 2 }),
                    higherFirstMonthlyRate: formatNumber(possibleDailyContributions + netChargeEmployee, {
                      digits: 2,
                    }),
                  }}
                ></Trans>
              </p>
            </Details>
          </div>
        </div>
      ) : null}

      <div className={detailsContentClass}>
        <div>
          <Heading as="h3">{t("calculationDetails.endOfContract.1")}</Heading>
          <p className={styles.description}>{t("calculationDetails.endOfContract.2")}</p>
        </div>
        <table className={styles.calculationDetailsTable}>
          <tbody>
            <ExpandableAmountRow
              label={t("calculationDetails.endOfContract.3")}
              value={subsidyDependantCalculationDetails?.restRate}
              id="calculationDetailsExpandableRow-restRate"
            >
              <p>
                {t("calculationDetails.endOfContract.explanation", {
                  residualFactor: formatNumber(state.configuration.leasingSetting.residualFactor, { digits: 2 }),
                  basePrice: t(`calculationDetails.endOfContract.${basePriceForResidual}`),
                })}
              </p>
            </ExpandableAmountRow>
          </tbody>
        </table>
      </div>
    </>
  );
};

function presentAsNegative(numberToBeNegative: number | undefined): number | undefined {
  return numberToBeNegative && -numberToBeNegative;
}

function getDisplayedServiceMonthlyRate(
  servicesMonthlyRate: number | undefined,
  grant: number,
  deductGrantFromServicePrices: boolean,
): number | undefined {
  if (servicesMonthlyRate === undefined || !deductGrantFromServicePrices) {
    return servicesMonthlyRate;
  }

  return Math.max(servicesMonthlyRate - grant, 0);
}

export { CalculationDetails };
